import React, { useState } from 'react';

import { PrefTypeOptions } from '@liscio/api';
import { Accordion, Icon, Stack, Typography } from '@liscio/ui';

import { OptionalFieldsBadge } from './CreateAccountFormFieldPrimitives';
import { CreateAccountAddressFields } from './sections/CreateAccountAddressFields';
import { CreateAccountBankAccountsFields } from './sections/CreateAccountBankAccountsFields';
import { CreateAccountBusinessDetailsFields } from './sections/CreateAccountBusinessDetailsFields';
import { CreateAccountContactFields } from './sections/CreateAccountContactFields';
import { CreateAccountDependentsFields } from './sections/CreateAccountDependentsFields';
import { CreateAccountSpouseDetailsFields } from './sections/CreateAccountSpouseDetailsFields';
import { CreateAccountTaxpayerDetailsFields } from './sections/CreateAccountTaxpayerDetailsFields';
import { CreateAccountTechnologySolutionsFields } from './sections/CreateAccountTechnologySolutionsFields';
import { CreateAccountFormLayout } from './types';

export interface CreateAccountAdditionalDetailsSectionProps {
  layout?: CreateAccountFormLayout;
  prefTypeOptions?: Pick<
    PrefTypeOptions,
    | 'referral_source'
    | 'account_rating'
    | 'industry'
    | 'filing_status'
    | 'dependent_relationship'
  >;
}

/**
 * Collapsible optional account fields grouped by category.
 * Collapsed by default; expands when the user clicks the summary row.
 */
export const CreateAccountAdditionalDetailsSection: React.FC<
  CreateAccountAdditionalDetailsSectionProps
> = ({ layout = 'responsive', prefTypeOptions }) => {
  const [expandAdditionalDetails, setExpandAdditionalDetails] = useState(false);

  return (
    <Accordion
      expanded={expandAdditionalDetails}
      onChange={(_, expanded) => setExpandAdditionalDetails(expanded)}
      expandIcon={{
        collapsed: <Icon icon="chevronDown" fontSize="small" />,
        expanded: <Icon icon="chevronUp" fontSize="small" />,
      }}
      summary={
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.5}
          width="100%"
          pr={1}
        >
          <Typography fontWeight={700} variant="body1">
            Additional details
          </Typography>
          <OptionalFieldsBadge />
        </Stack>
      }
      sx={{
        '&:before': { display: 'none' },
        bgcolor: 'transparent',
        borderTop: 1,
        borderBottom: 'none',
        borderColor: 'divider',
        boxShadow: 'none',
        mt: 1,
      }}
      summaryProps={{
        sx: {
          px: 0,
          minHeight: 52,
          '& .MuiAccordionSummary-content': { my: 1 },
        },
      }}
      detailsProps={{ sx: { px: 0, pt: 2.5, pb: 1 } }}
    >
      <Stack spacing={3} width="100%">
        <CreateAccountContactFields
          layout={layout}
          prefTypeOptions={prefTypeOptions}
        />
        <CreateAccountAddressFields layout={layout} />

        <CreateAccountBusinessDetailsFields
          layout={layout}
          prefTypeOptions={prefTypeOptions}
        />
        <CreateAccountTechnologySolutionsFields layout={layout} />
        <CreateAccountBankAccountsFields layout={layout} />
        <CreateAccountTaxpayerDetailsFields
          layout={layout}
          prefTypeOptions={prefTypeOptions}
        />
        <CreateAccountSpouseDetailsFields layout={layout} />
        <CreateAccountDependentsFields
          layout={layout}
          prefTypeOptions={prefTypeOptions}
        />
      </Stack>
    </Accordion>
  );
};

export default CreateAccountAdditionalDetailsSection;
